import { useState, useEffect, useRef } from "react";
import ScrollReveal from "./ScrollReveal";

const AIFeatures = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      id: 1,
      title: "AI-Powered Ad Optimization",
      description: "Our machine learning algorithms continuously analyze and optimize your META and Google Ads campaigns in real-time, maximizing ROI and reducing wasted spend.",
      icon: "🎯",
      stats: ["3x Better ROI", "50% Cost Reduction", "Real-time Optimization"],
      color: "from-blue-500 to-indigo-600",
      image: "📊"
    },
    {
      id: 2,
      title: "Custom Software Solutions",
      description: "Build scalable, high-performance applications tailored to your business needs. From web apps to enterprise systems, we deliver solutions that drive growth.",
      icon: "💻",
      stats: ["100% Custom Built", "Cloud-Native", "Scalable Architecture"],
      color: "from-purple-500 to-pink-600",
      image: "⚙️"
    },
    {
      id: 3,
      title: "Mobile App Development",
      description: "Create stunning iOS and Android applications that engage users and deliver exceptional experiences. Native performance with cross-platform efficiency.",
      icon: "📱",
      stats: ["iOS & Android", "Native Performance", "App Store Ready"],
      color: "from-green-500 to-emerald-600",
      image: "📲"
    },
    {
      id: 4,
      title: "Intelligent Automation",
      description: "Leverage AI and machine learning to automate repetitive tasks, predict customer behavior, and make data-driven decisions that accelerate your business.",
      icon: "🤖",
      stats: ["80% Time Saved", "Predictive Analytics", "24/7 Automation"],
      color: "from-orange-500 to-red-600",
      image: "🧠"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative py-24 bg-gradient-to-br from-gray-50 to-indigo-50 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float-delayed"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <ScrollReveal direction="up" duration={1000}>
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 border border-indigo-200 mb-6">
              <span className="w-2 h-2 bg-indigo-600 rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-medium text-indigo-700 uppercase tracking-wider">
                AI-Powered Solutions
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Intelligent Features That
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Transform Your Business
            </h3>
          </div>
        </ScrollReveal>

        {/* Features Grid with Alternating Animations */}
        <div className="space-y-32">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* Content Side */}
              <ScrollReveal
                direction={index % 2 === 0 ? "left" : "right"}
                delay={200}
                duration={1000}
                className={index % 2 === 1 ? "md:order-2" : ""}
              >
                <div className="space-y-6">
                  {/* Icon Badge */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.color} shadow-lg`}>
                    <span className="text-3xl">{feature.icon}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 pt-6">
                    {feature.stats.map((stat, idx) => (
                      <div
                        key={idx}
                        className="text-center p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300"
                      >
                        <div className={`text-sm font-semibold bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                          {stat}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button className={`group mt-6 inline-flex items-center px-6 py-3 rounded-xl bg-gradient-to-r ${feature.color} text-white font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105`}>
                    Learn More
                    <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </div>
              </ScrollReveal>

              {/* Visual Side */}
              <ScrollReveal
                direction={index % 2 === 0 ? "right" : "left"}
                delay={400}
                duration={1000}
                className={index % 2 === 1 ? "md:order-1" : ""}
              >
                <div className="relative">
                  {/* Main Card */}
                  <div className={`relative bg-gradient-to-br ${feature.color} rounded-3xl p-8 shadow-2xl overflow-hidden`}>
                    {/* Animated Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] bg-[length:250%_250%] animate-shimmer"></div>
                    </div>

                    {/* Content */}
                    <div className="relative z-10 text-center py-12">
                      <div className="text-8xl mb-6 animate-bounce-slow">
                        {feature.image}
                      </div>
                      <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="h-3 bg-white/30 rounded-full backdrop-blur-sm"
                            style={{
                              width: `${100 - i * 15}%`,
                              margin: "0 auto",
                              animation: `pulse ${2 + i * 0.5}s ease-in-out infinite`
                            }}
                          ></div>
                        ))}
                      </div>
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute top-4 right-4 w-12 h-12 bg-white/20 rounded-full backdrop-blur-sm animate-ping"></div>
                    <div className="absolute bottom-4 left-4 w-8 h-8 bg-white/20 rounded-full backdrop-blur-sm animate-pulse"></div>
                  </div>

                  {/* Decorative Elements */}
                  <div className={`absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r ${feature.color} rounded-full blur-2xl opacity-50 animate-pulse`}></div>
                  <div className={`absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-r ${feature.color} rounded-full blur-2xl opacity-30 animate-pulse`}></div>
                </div>
              </ScrollReveal>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <ScrollReveal direction="up" delay={200} duration={1000}>
          <div className="mt-24 text-center">
            <div className="inline-block bg-white rounded-3xl shadow-2xl p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Transform Your Business?
              </h3>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl">
                Let's discuss how our AI-powered solutions can help you achieve your goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-8 py-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  Start Your Project
                </button>
                <button className="px-8 py-4 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:border-indigo-600 hover:text-indigo-600 transition-all duration-300 transform hover:scale-105">
                  Schedule Demo
                </button>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -250% 0; }
          100% { background-position: 250% 0; }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AIFeatures;
